import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import CookieIconLogo from '../assets/CookieIconLogo.jpeg'
import {
  ArchiveBoxIcon,
  DocumentArrowDownIcon,
  HeartIcon,
} from '@heroicons/react/24/outline'

type CookieCutter = Database['public']['Tables']['cookie_cutters']['Row']
type StorageBin = Database['public']['Tables']['storage_bins']['Row']

interface Stats {
  totalCookieCutters: number
  totalStorageBins: number
}

interface RecentActivity {
  id: string
  type: 'cookie_cutter' | 'storage_bin'
  name: string
  created_at: string
  photo_url?: string
}

export default function Dashboard() {
  const { user, profile } = useAuth()
  const [stats, setStats] = useState<Stats>({
    totalCookieCutters: 0,
    totalStorageBins: 0,
  })
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    if (!user) return
    
    try {
      // Get cookie cutters count
      const { count: totalCookieCutters } = await supabase
        .from('cookie_cutters')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)

      // Get storage bins count
      const { count: totalStorageBins } = await supabase
        .from('storage_bins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)

      setStats({
        totalCookieCutters: totalCookieCutters || 0,
        totalStorageBins: totalStorageBins || 0,
      })

      // Fetch recent activity (last 10 items)
      const recentItems: RecentActivity[] = []

      // Get recent cookie cutters
      const { data: recentCookieCutters } = await supabase
        .from('cookie_cutters')
        .select('id, name, created_at, photo_url')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (recentCookieCutters) {
        recentItems.push(...recentCookieCutters.map((item: CookieCutter) => ({
          ...item,
          type: 'cookie_cutter' as const
        })))
      }

      // Get recent storage bins
      const { data: recentStorageBins } = await supabase
        .from('storage_bins')
        .select('id, name, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (recentStorageBins) {
        recentItems.push(...recentStorageBins.map((item: StorageBin) => ({
          ...item,
          type: 'storage_bin' as const
        })))
      }

      // Sort all items by created_at and take the most recent 10
      const sortedActivity = recentItems
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10)

      setRecentActivity(sortedActivity)
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const statCards = [
    {
      name: 'Total Cookie Cutters',
      value: stats.totalCookieCutters,
      icon: HeartIcon,
      gradient: 'from-secondary to-brown-sugar',
      link: '/inventory',
    },
    {
      name: 'Storage Bins',
      value: stats.totalStorageBins,
      icon: ArchiveBoxIcon,
      gradient: 'from-neutral to-cambridge-blue',
      link: '/storage-bins',
    },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <p className="text-gray-800 text-lg font-medium bg-white/95 backdrop-blur-md px-4 py-2 rounded-lg shadow-sm">
          Welcome back, <span className="font-bold text-gray-900">{profile?.display_name || user?.email}</span>!
          Here's what's happening with your cookie cutter collection.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
            {/* Inventory button spans full width on mobile, 2 cols on larger screens */}
            <Link
              to="/inventory"
              className="group relative flex items-center space-x-4 px-8 py-8 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 sm:col-span-2 lg:col-span-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 120" className="absolute inset-0 w-full h-full">
                <path
                  d="
                    M0,0
                    Q20,30 40,0
                    Q60,30 80,0
                    Q100,30 120,0
                    Q140,30 160,0
                    Q180,30 200,0
                    Q220,30 240,0
                    Q260,30 280,0
                    Q290,20 300,0

                    Q285,20 300,40
                    Q285,60 300,80
                    Q285,100 300,120

                    Q280,90 260,120
                    Q240,90 220,120
                    Q200,90 180,120
                    Q160,90 140,120
                    Q120,90 100,120
                    Q80,90 60,120
                    Q40,90 20,120
                    Q10,110 0,120

                    Q15,100 0,80
                    Q15,60 0,40
                    Q15,20 0,0
                    Z
                  "
                  className="fill-white/95 stroke-black"
                  strokeWidth="2"
                />
              </svg>
              <div className="flex-shrink-0 z-10 relative">
                <div className="bg-gradient-to-br from-secondary to-brown-sugar p-3 rounded-xl group-hover:shadow-lg transition-all duration-200">
                  <HeartIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0 z-10 relative">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-bold text-primary">View Inventory</p>
                <p className="text-sm text-gray-700">Search and manage your cookie cutters</p>
              </div>
            </Link>

            <Link
              to="/storage-bins"
              className="group relative flex items-center space-x-4 px-8 py-8 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 120" className="absolute inset-0 w-full h-full">
                <path
                  d="
                    M0,0
                    Q20,30 40,0
                    Q60,30 80,0
                    Q100,30 120,0
                    Q140,30 160,0
                    Q180,30 200,0
                    Q220,30 240,0
                    Q260,30 280,0
                    Q290,20 300,0

                    Q285,20 300,40
                    Q285,60 300,80
                    Q285,100 300,120

                    Q280,90 260,120
                    Q240,90 220,120
                    Q200,90 180,120
                    Q160,90 140,120
                    Q120,90 100,120
                    Q80,90 60,120
                    Q40,90 20,120
                    Q10,110 0,120

                    Q15,100 0,80
                    Q15,60 0,40
                    Q15,20 0,0
                    Z
                  "
                  className="fill-white/95 stroke-black"
                  strokeWidth="2"
                />
              </svg>
              <div className="flex-shrink-0 z-10 relative">
                <div className="bg-gradient-to-br from-neutral to-cambridge-blue p-3 rounded-xl group-hover:shadow-lg transition-all duration-200">
                  <ArchiveBoxIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0 z-10 relative">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-bold text-primary">Storage Bins</p>
                <p className="text-sm text-gray-700">Organize by storage location</p>
              </div>
            </Link>

            <Link
              to="/measurement-sheet"
              className="group relative flex items-center space-x-4 px-8 py-8 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 120" className="absolute inset-0 w-full h-full">
                <path
                  d="
                    M0,0
                    Q20,30 40,0
                    Q60,30 80,0
                    Q100,30 120,0
                    Q140,30 160,0
                    Q180,30 200,0
                    Q220,30 240,0
                    Q260,30 280,0
                    Q290,20 300,0

                    Q285,20 300,40
                    Q285,60 300,80
                    Q285,100 300,120

                    Q280,90 260,120
                    Q240,90 220,120
                    Q200,90 180,120
                    Q160,90 140,120
                    Q120,90 100,120
                    Q80,90 60,120
                    Q40,90 20,120
                    Q10,110 0,120

                    Q15,100 0,80
                    Q15,60 0,40
                    Q15,20 0,0
                    Z
                  "
                  className="fill-white/95 stroke-black"
                  strokeWidth="2"
                />
              </svg>
              <div className="flex-shrink-0 z-10 relative">
                <div className="bg-gradient-to-br from-accent to-slate-gray p-3 rounded-xl group-hover:shadow-lg transition-all duration-200">
                  <DocumentArrowDownIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0 z-10 relative">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-bold text-primary">Measurement Sheet</p>
                <p className="text-sm text-gray-700">Generate ruler sheet for size reference</p>
              </div>
            </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon
          const cardContent = (
            <div className="p-6 bg-white/95 backdrop-blur-sm rounded-2xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`bg-gradient-to-br ${card.gradient} p-3 rounded-xl shadow-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-accent truncate">
                      {card.name}
                    </dt>
                    <dd>
                      <div className="text-2xl font-bold text-primary">
                        {loading ? (
                          <div className="animate-pulse bg-neutral/30 rounded h-6 w-8"></div>
                        ) : (
                          card.value
                        )}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          )

          if (card.link) {
            return (
              <Link
                key={card.name}
                to={card.link}
                className="overflow-hidden shadow-lg rounded-2xl hover:shadow-xl hover:scale-105 transition-all duration-200 cursor-pointer border-2 border-black focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
              >
                {cardContent}
              </Link>
            )
          }

          return (
            <div key={card.name} className="overflow-hidden shadow-lg rounded-2xl border-2 border-black">
              {cardContent}
            </div>
          )
        })}
      </div>

      {/* Recent Activity */}
      <div className="mt-8 bg-white/95 backdrop-blur-md shadow-xl rounded-2xl border border-neutral/10">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-gradient-to-br from-neutral to-cambridge-blue p-2 rounded-xl">
              <span className="text-xl">📈</span>
            </div>
            <h3 className="text-xl font-bold text-primary">
              Recent Activity
            </h3>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse space-y-4">
                <div className="bg-neutral/20 h-4 rounded w-48 mx-auto"></div>
                <div className="bg-neutral/20 h-4 rounded w-32 mx-auto"></div>
              </div>
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gradient-to-br from-background to-tea-green/50 rounded-2xl p-8">
                <img src={CookieIconLogo} alt="The Cutter Keeper" className="w-full max-w-md mb-4 mx-auto rounded-lg shadow-lg" />
                <p className="text-gray-700 text-lg">No recent activity yet. Start by adding some cookie cutters or storage bins!</p>
              </div>
            </div>
          ) : (
            <div className="flow-root">
              <ul className="-mb-8">
                {recentActivity.map((item, itemIdx) => (
                  <li key={`${item.type}-${item.id}`}>
                    <div className="relative pb-8">
                      {itemIdx !== recentActivity.length - 1 ? (
                        <span
                          className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gradient-to-b from-neutral to-cambridge-blue/50"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="relative flex space-x-4">
                        <div>
                          <span className={`h-10 w-10 rounded-xl flex items-center justify-center ring-4 ring-surface shadow-lg ${
                            item.type === 'cookie_cutter' 
                              ? 'bg-gradient-to-br from-secondary to-brown-sugar' 
                              : 'bg-gradient-to-br from-neutral to-cambridge-blue'
                          }`}>
                            {item.type === 'cookie_cutter' ? (
                              <HeartIcon className="h-5 w-5 text-white" />
                            ) : (
                              <ArchiveBoxIcon className="h-5 w-5 text-white" />
                            )}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <Link
                            to={item.type === 'cookie_cutter' ? '/inventory' : '/storage-bins'}
                            className="flex items-center space-x-4 hover:bg-neutral/10 -mx-2 px-3 py-2 rounded-xl transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
                          >
                            {item.photo_url && (
                              <img
                                className="h-10 w-10 rounded-lg object-cover shadow-md border border-neutral/20"
                                src={item.photo_url}
                                alt={item.name}
                              />
                            )}
                            <div className="flex-1">
                              <p className="text-gray-700">
                                Added {item.type === 'cookie_cutter' ? 'cookie cutter' : 'storage bin'}{' '}
                                <span className="font-semibold text-primary hover:text-secondary transition-colors duration-200">{item.name}</span>
                              </p>
                            </div>
                          </Link>
                          <div className="text-right text-sm whitespace-nowrap text-gray-700 flex-shrink-0 pt-1">
                            <time
                              dateTime={item.created_at}
                              className="bg-background/50 px-3 py-1 rounded-full text-xs font-medium"
                            >
                              {new Date(item.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </time>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}